import React, { useMemo } from 'react';
import { Pressable, View } from 'react-native';
import moment from 'moment';
import Text from '../../../components/text/Text';
import { useThemedStyles } from '../../../hooks/useThemedStyles';
import { STATUS } from '../../../../common/constants';
import { StatusBadge } from '../../../design/components';
import { getStatusLabel } from '../../../utils/statusLabel';

const LocationSetupListItem = ({ item, customProps }) => {
	const styles = useThemedStyles(createStyles);

	const dueDateLabel = useMemo(() => {
		if (!item?.dueDate) {
			return 'No due date';
		}

		return moment(item.dueDate).format('DD MMM YYYY');
	}, [item?.dueDate]);

	const typeLabel = useMemo(() => {
		const hasMachine = item?.machineSetups?.length;
		const hasMarket = item?.marketSetups?.length;

		if (hasMachine && hasMarket) {
			return 'Market & machine';
		}

		if (hasMachine) {
			return 'Machine';
		}

		if (hasMarket) {
			return 'Market';
		}

		return 'N/A';
	}, [item?.machineSetups?.length, item?.marketSetups?.length]);

	const statusLabel = getStatusLabel(item?.status, 'Unknown');

	const handlePress = () => {
		if (customProps?.onPressItem) {
			customProps.onPressItem(item);
		}
	};

	return (
		<Pressable
			accessibilityRole="button"
			style={({ pressed }) => [styles.container, pressed && styles.pressed]}
			onPress={handlePress}
		>
			<View style={styles.headerRow}>
				<Text style={styles.identifier} numberOfLines={1}>
					{item?.identifier || 'Location setup'}
				</Text>
				<StatusBadge
					label={statusLabel}
					tone={item?.status === STATUS.COMPLETE ? 'success' : 'neutral'}
					size="small"
				/>
			</View>
			<Text style={styles.subtleText} numberOfLines={1}>
				Location: {item?.location?.name || 'Unknown'}
			</Text>
			<Text style={styles.subtleText} numberOfLines={1}>
				Client: {item?.location?.client?.name || 'Unknown'}
			</Text>
			<Text style={styles.subtleText} numberOfLines={1}>
				Due {dueDateLabel}
			</Text>
			<Text style={styles.subtleText} numberOfLines={1}>
				Type: {typeLabel}
			</Text>
		</Pressable>
	);
};

const createStyles = ({ ui: { colors, type, space, radius } }) => ({
	container: {
		marginHorizontal: space.gutter,
		marginBottom: space.sm,
		paddingHorizontal: 14,
		paddingVertical: space.md,
		gap: space.xs,
		borderRadius: radius.card,
		borderWidth: 1,
		borderColor: colors.raisedBorder,
		backgroundColor: colors.raised,
	},
	pressed: {
		opacity: 0.85,
	},
	headerRow: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: space.md,
		marginBottom: space.xxs,
	},
	identifier: {
		...type.rowTitleSemibold,
		color: colors.text,
		flex: 1,
	},
	subtleText: {
		...type.secondary,
		color: colors.textSecondary,
	},
});

export default LocationSetupListItem;
